import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import Orb from '../components/Orb';

const BetaSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1rem;
  position: relative;
  overflow: hidden;
  background: #0f1117;

  @media (min-width: 768px) {
    padding: 2rem 2rem;
  }
`;

const OrbWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  padding: 1.5rem;
  margin-top: -20vh;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 2;

  @media (min-width: 768px) {
    padding: 2.5rem;
    border-radius: 24px;
    margin-top: -10vh;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.75rem;
  color: white;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 1rem;
  color: white;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #9089fc;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 1rem;
  color: white;
  
  option {
    background: #000;
    color: white;
  }
  
  optgroup {
    background: #0f1117;
    color: rgba(255, 255, 255, 0.6);
    font-style: normal;
    font-weight: 600;
    padding-top: 0.5rem;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background: #9089fc;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(144, 137, 252, 0.3);
  }
`;

const SuccessMessage = styled.div`
  color: #4ade80;
  text-align: center;
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: rgba(74, 222, 128, 0.1);
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  text-align: center;
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.1);
`;

const Beta = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const detectDeviceType = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase())) {
      return 'mobile';
    }
    return 'desktop';
  };

  const industries = [
    // Home Systems & Utilities
    { header: true, name: 'Home Systems & Utilities' },
    'HVAC',
    'Plumbing',
    'Electrical',
    'Garage Doors',

    // Security & Safety
    { header: true, name: 'Security & Safety' },
    'Locksmith',
    'Alarm & Smart Home',
    'Fire Protection',

    // Cleaning & Maintenance
    { header: true, name: 'Cleaning & Maintenance' },
    'Residential Cleaning',
    'Carpet Cleaning',
    'Window Cleaning',
    'Pressure Washing',
    'Post-Construction Cleaning',

    // Property & Structural Services
    { header: true, name: 'Property & Structural Services' },
    'Roofing',
    'Construction',
    'Handyman',
    'Painting',
    'Pool & Spa Services',

    // Outdoor & Landscaping
    { header: true, name: 'Outdoor & Landscaping' },
    'Lawn Care',
    'Landscaping',
    'Arborist/Tree Care',
    'Snow Removal',

    // Specialty Services
    { header: true, name: 'Specialty Services' },
    'Appliance Repair',
    'Junk Removal',
    'Pest Control',
    'Auto Glass',

    // Technical Services
    { header: true, name: 'Technical Services' },
    'IT Services',
    
    // Other
    { header: true, name: 'Other' },
    'Other Industry'
  ];

  const renderIndustryOptions = () => {
    let currentGroup = null;
    let options = [];
    
    industries.forEach((industry, index) => {
      if (industry.header) {
        if (currentGroup) {
          options.push('</optgroup>');
        }
        currentGroup = industry.name;
        options.push(`<optgroup label="${industry.name}" key="group-${index}">`);
      } else {
        const value = industry.toLowerCase().replace(/\s+/g, '_');
        options.push(
          `<option value="${value}" key="option-${index}">${industry}</option>`
        );
      }
    });
    
    if (currentGroup) {
      options.push('</optgroup>');
    }
    
    return options.join('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');
    
    // Get form data
    const formData = new FormData(e.target);
    const data = {
      company_name: formData.get('company_name'),
      work_email: formData.get('work_email'),
      phone_number: formData.get('phone_number'),
      industry: formData.get('industry'),
      device_type: detectDeviceType()
    };

    try {
      const response = await fetch('https://app.servtrackr.com/api/beta-inquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Something went wrong');
      }
      
      setSubmitStatus('success');
      e.target.reset(); // Clear form
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ServTrackr Beta - Service Business Management Platform</title>
        <meta property="og:title" content="ServTrackr Beta Access" />
        <meta property="og:description" content="Get early access to our platform and help shape the future of service business management." />
        <meta property="og:image" content="https://app.servtrackr.com/servtrackrbanner.png" />
        <meta property="og:url" content="https://app.servtrackr.com/beta" />
        <meta property="og:type" content="website" />
        
        {/* For iMessage and Apple specific */}
        <meta name="apple-mobile-web-app-title" content="ServTrackr Beta" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
      </Helmet>

      <BetaSection>
        <OrbWrapper>
          <Orb 
            hue={240} 
            hoverIntensity={0.3} 
            rotateOnHover={false}
            forceHoverState={true}
          />
        </OrbWrapper>
        <Container>
          <Title>ServTrackr Beta</Title>
          <Description>
            Get early access to our platform and help shape the future of service business management.
          </Description>
          <Form onSubmit={handleSubmit}>
            <Input 
              name="company_name"
              type="text" 
              placeholder="Company Name" 
              required 
              disabled={isSubmitting}
            />
            <Input 
              name="work_email"
              type="email" 
              placeholder="Work Email" 
              required 
              disabled={isSubmitting}
            />
            <Input 
              name="phone_number"
              type="tel" 
              placeholder="Phone Number" 
              required 
              disabled={isSubmitting}
            />
            <Select 
              name="industry" 
              required 
              disabled={isSubmitting}
              dangerouslySetInnerHTML={{ __html: `
                <option value="">Select Industry</option>
                ${renderIndustryOptions()}
              `}}
            />
            <Button 
              type="submit" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Access'}
            </Button>
            
            {submitStatus === 'success' && (
              <SuccessMessage>
                Thank you for your interest! We'll be in touch soon.
              </SuccessMessage>
            )}
            
            {submitStatus === 'error' && (
              <ErrorMessage>
                {errorMessage || 'Something went wrong. Please try again later.'}
              </ErrorMessage>
            )}
          </Form>
        </Container>
      </BetaSection>
    </>
  );
};

export default Beta; 